import { ARCHIVE_CAMPAIGN, CREATE_CAMPAIGN, UNARCHIVE_CAMPAIGN } from "@lib/graphql/mutations";
import { CAMPAIGNS_QUERY, CLIENTS_QUERY } from "@lib/graphql/queries";
import { useCampaignListChannel } from "@lib/hooks/useCampaignListChannel";
import { type Connection, usePaginatedList } from "@lib/hooks/usePaginatedList";
import { type CampaignSort, type CampaignSortBy, useUIStore } from "@lib/stores/uiStore";
import type { Campaign, Client } from "@lib/types";
import { Archive, ArchiveRestore, ArrowDown, ArrowUp, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useMutation, useQuery } from "urql";
import { Button } from "../shared/Button";
import { PageHeader } from "../shared/PageHeader";
import { StatusPill } from "../shared/StatusPill";

const TEXT_COLUMNS: CampaignSortBy[] = ["TITLE", "CLIENT", "STATUS"];

function nextSortFor(current: CampaignSort, column: CampaignSortBy): CampaignSort {
  if (current.sortBy === column) {
    return { sortBy: column, sortDirection: current.sortDirection === "ASC" ? "DESC" : "ASC" };
  }
  return { sortBy: column, sortDirection: TEXT_COLUMNS.includes(column) ? "ASC" : "DESC" };
}

function formatLastAccessed(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso);
  const now = new Date();
  const seconds = Math.max(0, Math.floor((now.getTime() - then.getTime()) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return then.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

interface SortableHeaderProps {
  label: string;
  column: CampaignSortBy;
  active: boolean;
  direction: "ASC" | "DESC";
  onSort: (column: CampaignSortBy) => void;
}

function SortableHeader({ label, column, active, direction, onSort }: SortableHeaderProps) {
  const Arrow = direction === "ASC" ? ArrowUp : ArrowDown;
  const ariaSort: React.AriaAttributes["aria-sort"] = active
    ? direction === "ASC"
      ? "ascending"
      : "descending"
    : "none";
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted"
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className="flex items-center gap-1 uppercase tracking-wide hover:text-text-primary"
      >
        {label}
        {active && <Arrow size={12} aria-hidden />}
      </button>
    </th>
  );
}

function ArchivePopover({
  campaignId,
  onDone,
  onCancel,
}: {
  campaignId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState("");
  const [, archiveCampaign] = useMutation(ARCHIVE_CAMPAIGN);
  const [submitting, setSubmitting] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onCancel();
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onCancel]);

  async function handleArchive() {
    setSubmitting(true);
    const result = await archiveCampaign({
      input: { campaignId, reason: reason.trim() || null },
    });
    setSubmitting(false);
    if (result.data?.archiveCampaign?.errors?.length === 0) onDone();
  }

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full z-10 mt-1 w-64 rounded-lg border border-border bg-card p-3 shadow-lg"
    >
      <label
        htmlFor={`archive-reason-${campaignId}`}
        className="block text-xs font-medium text-text-secondary"
      >
        Reason (optional)
      </label>
      <input
        id={`archive-reason-${campaignId}`}
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. Role filled, on hold"
        className="mt-1 block w-full rounded border border-input-border bg-card px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
        onKeyDown={(e) => {
          if (e.key === "Enter") handleArchive();
          if (e.key === "Escape") onCancel();
        }}
      />
      <div className="mt-2 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleArchive}
          disabled={submitting}
          className="rounded bg-accent px-2 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
        >
          {submitting ? "Archiving..." : "Archive"}
        </button>
      </div>
    </div>
  );
}

export function CampaignList() {
  const campaignSort = useUIStore((s) => s.campaignSort);
  const setCampaignSort = useUIStore((s) => s.setCampaignSort);
  const [searchParams, setSearchParams] = useSearchParams();
  const showArchived = searchParams.get("view") === "archived";
  const {
    nodes: campaigns,
    fetching,
    hasNextPage,
    loadMore,
    refresh,
  } = usePaginatedList<{ campaigns: Connection<Campaign> }, Campaign>({
    query: CAMPAIGNS_QUERY,
    variables: {
      archived: showArchived,
      sortBy: campaignSort.sortBy,
      sortDirection: campaignSort.sortDirection,
    },
    getConnection: (data) => data.campaigns,
  });
  // Client picker: one large page (field max_page_size 250), not paginated.
  const [{ data: clientsData }] = useQuery({
    query: CLIENTS_QUERY,
    variables: { first: 250 },
  });
  const [, createCampaign] = useMutation(CREATE_CAMPAIGN);
  const [, unarchiveCampaign] = useMutation(UNARCHIVE_CAMPAIGN);
  useCampaignListChannel(refresh);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [clientId, setClientId] = useState("");
  const [briefNotes, setBriefNotes] = useState("");
  const [hiringManagerNotes, setHiringManagerNotes] = useState("");
  const [otherNotes, setOtherNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const navigate = useNavigate();

  const clients: Client[] = (clientsData?.clients?.edges ?? [])
    .map((edge: { node: Client }) => edge.node)
    .toSorted((a: Client, b: Client) => a.name.localeCompare(b.name));

  const firstClientId = clients[0]?.id ?? "";

  useEffect(() => {
    if (firstClientId && clientId === "") {
      setClientId(firstClientId);
    }
  }, [firstClientId, clientId]);

  function resetForm() {
    setTitle("");
    setClientId("");
    setBriefNotes("");
    setHiringManagerNotes("");
    setOtherNotes("");
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    const result = await createCampaign({
      title,
      clientId,
      briefNotes: briefNotes.trim() || null,
      hiringManagerNotes: hiringManagerNotes.trim() || null,
      otherNotes: otherNotes.trim() || null,
    });
    setSubmitting(false);
    const created = result.data?.createCampaign;
    if (created?.errors?.length === 0 && created.campaign?.id) {
      setShowForm(false);
      resetForm();
      navigate(`/campaigns/${created.campaign.id}`);
    }
  }

  async function handleUnarchive(campaignId: string) {
    const result = await unarchiveCampaign({ input: { campaignId } });
    if (result.data?.unarchiveCampaign?.errors?.length === 0) refresh();
  }

  function handleSort(column: CampaignSortBy) {
    setCampaignSort(nextSortFor(campaignSort, column));
  }

  return (
    <div>
      <PageHeader
        title="Campaigns"
        subtitle="Manage your search engagements"
        actions={
          !showArchived ? (
            <Button onClick={() => setShowForm(!showForm)} disabled={clients.length === 0}>
              <Plus size={16} /> New Campaign
            </Button>
          ) : undefined
        }
      />

      <nav className="mt-4 flex gap-1 border-b border-border">
        <button
          type="button"
          onClick={() => setSearchParams({}, { replace: true })}
          className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
            !showArchived
              ? "border-accent text-text-primary"
              : "border-transparent text-text-muted hover:text-text-secondary"
          }`}
        >
          Active
        </button>
        <button
          type="button"
          onClick={() => setSearchParams({ view: "archived" }, { replace: true })}
          className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
            showArchived
              ? "border-accent text-text-primary"
              : "border-transparent text-text-muted hover:text-text-secondary"
          }`}
        >
          Archived
        </button>
      </nav>

      {showForm && !showArchived && (
        <form
          onSubmit={handleCreate}
          className="mt-4 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring"
          aria-label="New campaign"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="new-campaign-title"
                className="block text-sm font-medium text-text-secondary"
              >
                Title *
              </label>
              <input
                id="new-campaign-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
            <div>
              <label
                htmlFor="new-campaign-client"
                className="block text-sm font-medium text-text-secondary"
              >
                Client *
              </label>
              <select
                id="new-campaign-client"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              >
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4">
            <label
              htmlFor="new-campaign-brief-notes"
              className="block text-sm font-medium text-text-secondary"
            >
              Brief notes
            </label>
            <textarea
              id="new-campaign-brief-notes"
              value={briefNotes}
              onChange={(e) => setBriefNotes(e.target.value)}
              rows={6}
              placeholder="Paste the role brief or any rough notes — we'll structure them for you."
              className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="new-campaign-hm-notes"
                className="block text-sm font-medium text-text-secondary"
              >
                Hiring manager notes
              </label>
              <textarea
                id="new-campaign-hm-notes"
                value={hiringManagerNotes}
                onChange={(e) => setHiringManagerNotes(e.target.value)}
                rows={3}
                placeholder="What you know about the hiring manager — their priorities, style, history."
                className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
            <div>
              <label
                htmlFor="new-campaign-other-notes"
                className="block text-sm font-medium text-text-secondary"
              >
                Other notes
              </label>
              <textarea
                id="new-campaign-other-notes"
                value={otherNotes}
                onChange={(e) => setOtherNotes(e.target.value)}
                rows={3}
                placeholder="Anything else — search constraints, dealbreakers, context."
                className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
          </div>
          <p className="mt-3 text-xs text-text-muted">
            If you provide any notes, the brief and personas will be drafted in the background.
          </p>
          <div className="mt-4 flex gap-2">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Creating…" : "Create"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShowForm(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {fetching && campaigns.length === 0 ? (
        <p className="mt-4 text-sm text-text-muted">Loading...</p>
      ) : campaigns.length === 0 ? (
        <div className="mt-4 rounded-lg border-2 border-dashed border-border-dashed p-8 text-center">
          <p className="text-sm text-text-muted">
            {showArchived
              ? "No archived campaigns."
              : clients.length === 0
                ? "Add a client first, then create your first campaign."
                : "No campaigns yet. Create your first campaign."}
          </p>
        </div>
      ) : (
        <div className="mt-4 overflow-hidden rounded-lg bg-card shadow-sm ring-1 ring-ring">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-card-alt">
              <tr>
                <SortableHeader
                  label="Campaign"
                  column="TITLE"
                  active={campaignSort.sortBy === "TITLE"}
                  direction={campaignSort.sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Client"
                  column="CLIENT"
                  active={campaignSort.sortBy === "CLIENT"}
                  direction={campaignSort.sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Status"
                  column="STATUS"
                  active={campaignSort.sortBy === "STATUS"}
                  direction={campaignSort.sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Stage"
                  column="STAGE"
                  active={campaignSort.sortBy === "STAGE"}
                  direction={campaignSort.sortDirection}
                  onSort={handleSort}
                />
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Candidates
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Lead
                </th>
                <SortableHeader
                  label="Last accessed"
                  column="RECENTLY_ACCESSED"
                  active={campaignSort.sortBy === "RECENTLY_ACCESSED"}
                  direction={campaignSort.sortDirection}
                  onSort={handleSort}
                />
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-text-muted">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border [&>tr:nth-child(even)]:bg-card-alt">
              {campaigns.map((campaign) => (
                <tr key={campaign.id} className="transition-colors hover:bg-card-hover">
                  <td className="px-4 py-3">
                    <Link
                      to={`/campaigns/${campaign.id}`}
                      className="font-medium text-link hover:text-link-hover"
                    >
                      {campaign.title}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary">{campaign.client.name}</td>
                  <td className="px-4 py-3">
                    <StatusPill status={campaign.status} />
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary tabular-nums">
                    {campaign.currentStage}/9
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-text-primary tabular-nums">
                    {campaign.candidateCount}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary">
                    {campaign.createdBy.name}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary">
                    {formatLastAccessed(campaign.lastAccessedAt)}
                  </td>
                  <td className="relative px-4 py-3 text-right">
                    {showArchived ? (
                      <button
                        type="button"
                        onClick={() => handleUnarchive(campaign.id)}
                        className="rounded border border-border p-1.5 text-text-faint hover:bg-card-hover hover:text-text-secondary"
                        title="Unarchive campaign"
                      >
                        <ArchiveRestore size={14} />
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            setArchivingId(archivingId === campaign.id ? null : campaign.id)
                          }
                          className="rounded border border-border p-1.5 text-text-faint hover:bg-card-hover hover:text-text-secondary"
                          title="Archive campaign"
                        >
                          <Archive size={14} />
                        </button>
                        {archivingId === campaign.id && (
                          <ArchivePopover
                            campaignId={campaign.id}
                            onDone={() => {
                              setArchivingId(null);
                              refresh();
                            }}
                            onCancel={() => setArchivingId(null)}
                          />
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasNextPage && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={loadMore}
            disabled={fetching}
            className="rounded-md border border-input-border bg-card px-4 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
          >
            {fetching ? "Loading..." : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}
